import { SearchResponse } from '@algolia/client-search';
import { database } from '@app/firebase';
import AppBackgroundColor from '@components/app-background-color/AppBackgroundColor';
import ButtonLink from '@components/button-link/ButtonLink';
import HomeCard from '@components/cards/HomeCard';
import HomeCardWide from '@components/cards/HomeCardWide';
import HomesCarousel from '@components/carousels/HomesCarousel';
import Hero from '@components/hero/Hero';
import awaitUserLogin from '@components/login/awaitUserLogin';
import SearchInput from '@components/search-input/SearchInput';
import TextMedia, { Media, Text } from '@components/text-media/TextMedia';
import UserContext from '@context/UserContext';
import useFacetSearch, { facetSearchServerSide } from '@hooks/useFacetSearch';
import { ContentBlock } from '@layouts/ContentBlocks';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import MuiPagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import IconArrowRight from '@ui/icons/imaterial/arrows/Arrow Right.svg';
import { addSavedHome, removeSavedHome } from '@utils/savedItems';
import { makeSeoData } from 'config/next-seo.config';
import { collection, getDocs, limit,orderBy, query as firestoreQuery, where } from 'firebase/firestore';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import React, { useEffect, useMemo } from 'react';
import { useContext } from 'react';
import { A11y, Mousewheel, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import useStyles from './homes.style';


const title = 'Staging a Home is Different with Guest House';
const description = 'When clients choose us, they find out staging a home is different with Guest House. Look at the houses we\'ve staged to see what we mean.';

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  const searchResults = await facetSearchServerSide({
    query, extraFacetFilters: {
      inStock: undefined,
    }
  });

  const coloradoVicinities = ['Boulder', 'Denver', 'Vail'];
  const californiaVicinities = ['San Diego', 'Coronado', 'Encinitas', 'Norwalk', 'Huntington Beach', 'Anaheim', 'Escondido', 'Mission Viejo'];
  const coloradoHomes = [] as guesthouse.Home[];
  const californiaHomes = [] as guesthouse.Home[];
  const featuredHomes = [] as guesthouse.Home[];

  const coloradoHomeDocs = await getDocs(firestoreQuery(collection(database, 'homes')
    ,where('published', '==', true)
    ,where('vicinity', 'in', coloradoVicinities)
    ,where('featured', '==', true)
    ,orderBy('install_date', 'desc')
    ,limit(10)));

  const californiaHomeDocs = await getDocs(firestoreQuery(collection(database, 'homes')
    ,where('published', '==', true)
    ,where('vicinity', 'in', californiaVicinities)
    ,where('featured', '==', true)
    ,orderBy('install_date', 'desc')
    ,limit(10)));

  // Query case studies - query without orderBy first to avoid index issues, then sort in memory
  let featuredHomesDocs;

  try {
    featuredHomesDocs = await getDocs(firestoreQuery(collection(database, 'homes')
      ,where('published', '==', true)
      ,where('case_study', '==', true)));
  } catch (error) {
    // If query fails (e.g., missing index), try without published filter
    console.error('Error fetching case studies with published filter:', error);
    try {
      featuredHomesDocs = await getDocs(firestoreQuery(collection(database, 'homes')
        ,where('case_study', '==', true)));
    } catch (fallbackError) {
      console.error('Error fetching case studies:', fallbackError);
      featuredHomesDocs = { docs: [] };
    }
  }

  if (coloradoHomeDocs.docs.length) {
    for (const doc of coloradoHomeDocs.docs) {
      coloradoHomes.push(doc.data() as guesthouse.Home);
    }
  }

  if (californiaHomeDocs.docs.length) {
    for (const doc of californiaHomeDocs.docs) {
      californiaHomes.push(doc.data() as guesthouse.Home);
    }
  }

  if (featuredHomesDocs.docs.length) {
    const allCaseStudies = featuredHomesDocs.docs.map(doc => doc.data() as guesthouse.Home)
      .filter(home => home.case_study === true); // Double-check filter
    
    // Sort in memory by install_date (descending)
    allCaseStudies.sort((a, b) => {
      const dateA = a.install_date?.seconds || a.install_date || 0;
      const dateB = b.install_date?.seconds || b.install_date || 0;

      return dateB - dateA;
    });
    
    // Limit to 10
    featuredHomes.push(...allCaseStudies.slice(0, 10));
  }

  return {
    props: {
      query,
      results: JSON.stringify(searchResults),
      coloradoHomes: JSON.stringify(coloradoHomes),
      californiaHomes: JSON.stringify(californiaHomes),
      featuredHomes: JSON.stringify(featuredHomes)
    },
  };
};

function Homes(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const coloradoHomes = JSON.parse(props.coloradoHomes) as guesthouse.Home[];
  const californiaHomes = JSON.parse(props.californiaHomes) as guesthouse.Home[];
  const featuredHomes = JSON.parse(props.featuredHomes) as guesthouse.Home[];

  const { classes, theme } = useStyles({ photosLength: 0 });
  const initResults = useMemo(() => JSON.parse(props.results), [props.results]) as SearchResponse<guesthouse.Home>;
  const userContext = useContext<UserContext>(UserContext);
  const { md } = useBreakpoints();
  const router = useRouter();

  const {
    results,
    setQuery,
    page,
    setPage,
    query,
  } = useFacetSearch<guesthouse.Home>({
    query: router.query,
    initResults,
    indexOptions: {
      init: 'homes_install_date_desc',
      offer_price_desc: 'homes',
      offer_price_asc: 'homes',
    },
    extraFacetFilters: {
      inStock: undefined,
    },
    hitsPerPage: 6,
  });

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (Object.keys(router.query).includes('page')) {
      setPage(Number(router.query['page']));
    }
  }, [router.isReady]);


  return (
    <>
      <AppBackgroundColor backgroundColor="#ede4e2" />
      <NextSeo {...makeSeoData({ pageTitle: title, metaTitle: title, metaDescription: description })} />
      <ContentBlock margin={[0, theme.spacing(3)]}>
        <Hero
          key="homes"
          backgroundImage="/images/shop-homes/homes-main.jpeg"
          className={classes.hero}
        >
          <Box
            textAlign="center"
            maxWidth={700}
          >
            <Typography
              variant={md ? 'display2' : 'display3'}
              component="h1"
              style={{
                color: theme.palette.common.white,
                textShadow: '0px 0px 20px rgba(0, 0, 0, .7)',
              }}
            >
              Find a Guest House near you
            </Typography>

            <Box
              display="flex"
              justifyContent="center"
              width="100%"
              marginTop={theme.spacing(3)}
            >
              <SearchInput
                placeholder="Enter home address"
                value={query}
                style={{
                  height: md ? 72 : 55,
                  width: md ? 424 : '100%'
                }}
                onValueChange={(value) => {
                  setQuery(value);
                  setPage(0);
                }}
              />
            </Box>
          </Box>
        </Hero>
      </ContentBlock>

      {query &&
      <ContentBlock
        margin={[2, 0, 0]}
        marginMd={[6, 0, 12]}
      >
        <Grid
          container
          spacing={4}
          style={{
            paddingTop: md ? theme.spacing(4.5) : 0,
            margin: 0,
            width: '100%'
          }}
        >
          {md &&
          <div
            style={{
              display: 'flex',
              flexDirection: md ? 'row' : 'column',
              alignItems: 'center'
            }}
          >
            <Typography
              style={{
                marginLeft: theme.spacing(1),
                marginRight: theme.spacing(4.5),
              }}
            >
              {results.nbHits}
              {' '}
              {results.nbHits === 1 ? 'HOME' : 'HOMES'}
            </Typography>
          </div>
        }
          {
          results.hits?.length
            ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: md ? 'row' : 'column',
                  justifyContent: md ? 'space-between' : 'center',
                  margin: 'auto'
                }}
              >
                {results?.page + 1 === results?.nbPages ?
                  <Typography style={{ margin: theme.spacing(3, 0), fontSize: 14 }}>
                    Showing
                    {' '}
                    {results.page === 0 ?
                      results.nbHits - (results.nbHits - 1)
                      :
                      results.nbHits - (results.nbHits - (results.hitsPerPage * results.page))
                    }
                    {' '}
                    -
                    {' '}
                    {results.nbHits}
                    {' '}
                    of
                    {' '}
                    {results.nbHits}
                  </Typography>
                  :
                  <Typography style={{ margin: theme.spacing(3, 0), fontSize: 14 }}>
                    Showing
                    {' '}
                    {results.hitsPerPage * (results.page + 1) - (results.hitsPerPage - 1)}
                    {' '}
                    -
                    {' '}
                    {results.hitsPerPage * (results.page + 1)}
                    {' '}
                    of
                    {' '}
                    {results.nbHits}
                  </Typography>
                }
                {results.nbPages > 1 &&
                  <div>
                    <MuiPagination
                      size={md ? 'medium' : 'small'}
                      count={results.nbPages}
                      page={page === 0 ? 1 : page}
                      style={{ margin: theme.spacing(3) }}
                      onChange={(e, value: number) => {
                        e.preventDefault();
                        setPage(value);
                      }}
                    />
                  </div>
                }
              </div>
            ) : (
              <Typography style={{ margin: theme.spacing(3, 0) }}>
                No homes match your search
              </Typography>
            )
        }

          <Grid
            container
            spacing={1.5}
          >
            {results?.hits?.length > 0 && results.hits.map((home) => {
              const saved = userContext.savedHomes?.ids.indexOf(home.docID) > -1;

              return (
                <Grid
                  key={home.docID}
                  item
                  xs={6}
                  md={4}
                >
                  <HomeCard
                    key={home.docID}
                    home={home}
                    saved={saved}
                    onSavedClick={async (e) => {
                      e.stopPropagation();

                      if (userContext.user) {
                        if (saved) {
                          removeSavedHome(userContext.user.uid, home);
                        } else {
                          addSavedHome(userContext.user.uid, home);
                        }
                      } else {
                        await awaitUserLogin(userContext)
                          .then((user) => {
                            addSavedHome(user.uid, home);
                          });
                      }
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>

          {results.nbPages > 1 &&
          <div>
            <MuiPagination
              size={md ? 'medium' : 'small'}
              count={results.nbPages}
              page={page === 0 ? 1 : page}
              style={{ margin: theme.spacing(3) }}
              onChange={(e, value: number) => {
                e.preventDefault();
                setPage(value);
              }}
            />
          </div>
        }

        </Grid>
      </ContentBlock>
}

      <ContentBlock
        maxWidth={'none'}
        style={{ overflowX: 'hidden' }}
        margin={0}
      >
        <ContentBlock
          margin={[0, theme.spacing(0)]}
          marginMd={[0, theme.spacing(12)]}
          padding={0}
          paddingMd={[0, theme.spacing(3)]}
        >
          {!!featuredHomes?.length && (
          <ContentBlock
            margin={[8, 0]}
            marginMd={[8, 0, 16]}
          >
            <Typography
              variant="h4Alt"
              component="h2"
              style={{ marginBottom: theme.spacing(4) }}
            >
              Case Studies
            </Typography>

            <Swiper
              navigation
              modules={[Pagination, Navigation, A11y, Mousewheel]}
              observer={true}
              observeParents={true}
              mousewheel={{
                forceToAxis: true,
              }}
              freeMode={true}
              slidesPerView="auto"
              spaceBetween={24}
              pagination={{
                clickable: true,
                type: 'progressbar',
              }}
              threshold={md ? 250 : 0}
            >
              {featuredHomes.map(home => {
                const saved = userContext.savedHomes?.ids.indexOf(home.docID) > -1;

                return (
                  <SwiperSlide
                    key={home.docID}
                    style={{ width: 'auto' }}
                  >
                    <HomeCardWide
                      key={home.docID}
                      home={home}
                      style={{ marginBottom: theme.spacing(2), width: md ? 1182 : 350 }}
                      saved={saved}
                      onSavedClick={async () => {
                        if (userContext.user) {
                          if (saved) {
                            removeSavedHome(userContext.user.uid, home);
                          } else {
                            addSavedHome(userContext.user.uid, home);
                          }
                        } else {
                          await awaitUserLogin(userContext)
                            .then((user) => {
                              addSavedHome(user.uid, home);
                            });
                        }
                      }}
                    />
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </ContentBlock>
          )}

          {!!coloradoHomes?.length && (
          <ContentBlock
            margin={[8, 0]}
            marginMd={[8, 0, 16]}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between'
              }}
            >
              <Typography
                variant="h4Alt"
                component="h2"
                style={{ marginBottom: theme.spacing(4) }}
              >
                Colorado
              </Typography>

              <Link
                passHref
                legacyBehavior
                href="/shop/homes/locations/colorado"
              >
                <Typography
                  component="a"
                  className={classes.seeAllLink}
                >
                  See all Colorado homes
                </Typography>
              </Link>
            </div>

            <HomesCarousel
              homes={coloradoHomes}
            />

          </ContentBlock>
          )}

          {!!californiaHomes?.length && (
          <ContentBlock
            margin={[8, 0]}
            marginMd={[8, 0, 16]}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between'
              }}
            >
              <Typography
                variant="h4Alt"
                component="h2"
                style={{ marginBottom: theme.spacing(4) }}
              >
                California
              </Typography>

              <Link
                passHref
                legacyBehavior
                href="/shop/homes/locations/california"
              >
                <Typography
                  component="a"
                  className={classes.seeAllLink}
                >
                  See all California homes
                </Typography>
              </Link>
            </div>

            <HomesCarousel
              homes={californiaHomes}
            />

          </ContentBlock>
          )}
        </ContentBlock>
      </ContentBlock>

      <ContentBlock>
        <TextMedia style={{ background: '#f6f6f7' }}>
          <Text style={{ justifyContent: 'center' }}>
            <Typography
              gutterBottom
              variant="h1"
              component="h2"
            >
              Stage a home
              <br />
              {' '}
              with Guest House
            </Typography>

            <Typography style={{ flex: 0, marginBottom: theme.spacing(6) }}>
              We&apos;re helping properties in Denver, Boulder, Colorado Springs and San Diego sell 40% faster. Let&apos;s work together.
            </Typography>

            <div>
              <Button
                size="large"
                variant="contained"
                color="primary"
                component={ButtonLink}
                href="/home-staging/get-a-quote"
                style={{
                  marginRight: theme.spacing(2)
                }}
              >
                Get a Free Quote
              </Button>

              <Button
                component={ButtonLink}
                href="/home-staging"
                size="large"
                style={{ color: theme.palette.tertiary.main, marginRight: theme.spacing(1) }}
              >
                How it Works
                <IconArrowRight
                  style={{
                    marginLeft: theme.spacing(1),
                    height: 18,
                    fill: theme.palette.tertiary.main,
                    position: 'relative',
                    top: 2,
                  }}
                />
              </Button>
            </div>
          </Text>

          <Media
            orientation="right"
          >
            <div
              style={{
                borderRadius: theme.shape.borderRadius,
                overflow: 'hidden',
                height: md ? 750 : 350,
                width: '100%',
                position: 'relative',
                top: md ? -90 : 0,
              }}
            >
              <Image
                fill
                src="/images/shop-homes/home-stage.jpg"
                alt=""
                sizes="100vw"
                style={{
                  objectFit: 'cover'
                }}
              />
            </div>
          </Media>
        </TextMedia>
      </ContentBlock>
    </>
  );
}


export default Homes;
